import React, { useState } from 'react';
import {
  Container,
  TextField,
  Checkbox,
  FormControlLabel,
  FormControl,
  Select,
  MenuItem,
  Box,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { RemovableField } from './RemovableField';
import { useDynamicFields } from './DynamicFieldsContext';
import { UploadButton } from '@frontend/features/common/components/UploadButton';
import { FileUpload } from '@mui/icons-material';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { client } from '@frontend/features/common/services';
import { FixedForm } from './FixedForm';
import { LoadingButton } from '@mui/lab';
import { FormSent } from './FormSent';

interface DynamicFormProps {
  title: string;
  agencyId: string;
  workflowId: string;
  editing?: boolean;
}

export const DynamicForm: React.FC<DynamicFormProps> = ({ title, agencyId, workflowId, editing }) => {
  const { fields, updateFields } = useDynamicFields();
  const { addToast } = useSnackBar();
  const [formValues, setFormValues] = useState<{ [key: string]: any }>({});
  const [isSending, setIsSending] = useState(false);
  const [sent, setSent] = useState(false);

  const { t } = useTranslation();

  const handleChange = (name: string, value: unknown) => {
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  const handleRemoveField = (removeIndex: number) => {
    updateFields(fields.filter((_, index) => index !== removeIndex));
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    try {
      e.preventDefault();
      setIsSending(true);
      if (!editing) {
        const formData = new FormData(e.currentTarget);
     
        await client.post('case/support', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      setSent(true);
      addToast({
        message: t('supportForms.send.success'),
        color: 'success',
      });
    } catch {
      addToast({
        message: t('supportForms.send.error'),
        color: 'error',
      });
    } finally {
      setIsSending(false);
    }
  };

  if (sent) {
    return <FormSent />;
  }

  return (
    <Container maxWidth="sm">
      <Box
        component="form"
        display="flex"
        justifyContent="flex-end"
        flexDirection="column"
        onSubmit={handleSubmit}
        gap={2}
      >
        {
          <FixedForm
            title={title}
            agencyId={agencyId}
            workflowId={workflowId}
            fields={fields}
            fieldValues={formValues}
          />
        }
        {fields.map((field, index) => {
          switch (field.fieldType) {
            case 'string':
              return (
                <RemovableField key={field.name} editing={editing} onEditing={() => handleRemoveField(index)}>
                  <TextField
                    fullWidth
                    margin="normal"
                    size="small"
                    label={field.label}
                    required={field.required}
                    value={formValues[field.name] || ''}
                    onChange={(e) => handleChange(field.name, e.target.value)}
                  />
                </RemovableField>
              );
            case 'longtext':
              return (
                <RemovableField key={field.name} editing={editing} onEditing={() => handleRemoveField(index)}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={5}
                    margin="normal"
                    size="small"
                    label={field.label}
                    required={field.required}
                    value={formValues[field.name] || ''}
                    onChange={(e) => handleChange(field.name, e.target.value)}
                  />
                </RemovableField>
              );
            case 'boolean':
              return (
                <RemovableField key={field.name} editing={editing} onEditing={() => handleRemoveField(index)}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formValues[field.name] || false}
                        onChange={(e) => handleChange(field.name, e.target.checked)}
                      />
                    }
                    label={field.label}
                  />
                </RemovableField>
              );
            case 'select':
              return (
                <RemovableField key={field.name} editing={editing} onEditing={() => handleRemoveField(index)}>
                  <FormControl fullWidth margin="normal">
                    <Select
                      displayEmpty
                      size="small"
                      value={formValues[field.name] || undefined}
                      onChange={(e) => handleChange(field.name, e.target.value)}
                      required={field.required}
                    >
                      <MenuItem key={field.label} value={undefined} disabled>
                        {field.label}
                      </MenuItem>
                      {field.options?.map((option, index) => (
                        <MenuItem key={index} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </RemovableField>
              );
            case 'date':
              return (
                <RemovableField key={field.name} editing={editing} onEditing={() => handleRemoveField(index)}>
                  <TextField
                    fullWidth
                    size="small"
                    margin="normal"
                    type="date"
                    label={field.label}
                    required={field.required}
                    InputLabelProps={{ shrink: true }}
                    value={formValues[field.name] || ''}
                    onChange={(e) => handleChange(field.name, e.target.value)}
                  />
                </RemovableField>
              );
            case 'file':
              return (
                <RemovableField key={field.name} editing={editing} onEditing={() => handleRemoveField(index)}>
                  <UploadButton
                    component="label"
                    variant="outlined"
                    startIcon={<FileUpload />}
                    name={field.name}
                    required={field.required}
                    onChange={(e) => handleChange(field.name, e.target.value)}
                  >
                    {field.label}
                  </UploadButton>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {formValues[field.name]?.split(/(\\|\/)/g).pop() || ''}
                  </Typography>
                </RemovableField>
              );
            default:
              return null;
          }
        })}
        <br />
        <LoadingButton
          type="submit"
          variant="contained"
          color="primary"
          loading={isSending}
        >
          {t('common.actions.send')}
        </LoadingButton>
      </Box>
    </Container>
  );
};
